import parse, { Element } from 'html-react-parser';
import { NewsProvider } from '@/constants/lookups';
import { getCurrentTheme } from '@/themes';
import { formatPercentage } from './numberUtils';

// Enums
enum NewsArticleDataType {
    Stock = 'stock',
    Article = 'article',
}

// Interfaces
export interface NewsArticleData {
    type: string;
    value: string;
    title?: string;
    provider?: NewsProvider;
}

export const parseHtml = (
    body?: string,
    options?: {
        articleQuotes?: { symbol: string; changePercent: number }[];
        data?: Record<string | number, NewsArticleData>;
        onArticleClick?: (articleData: NewsArticleData) => void;
        onSymbolClick?: (symbol: string) => void;
    },
) => {
    if (!body) {
        return '';
    }

    const dataNodeClassname = 'data-node';

    if (options?.articleQuotes) {
        return parse(
            body.replace(/((NYSE|Nasdaq|OTC):\s?)?{(?<index>\d+)}/gi, (...args) => {
                const match = args[0];
                const groups = args[args.length - 1];
                const dataIndex = groups.index;
                const articleData = options.data?.[dataIndex];

                if (!articleData) {
                    return match;
                }

                return `<span class="${dataNodeClassname}" data-index="${dataIndex}"></span>`;
            }),
            {
                replace: (domNode) => {
                    if (domNode instanceof Element && domNode.attribs.class === dataNodeClassname) {
                        const dataIndex = Number(domNode.attribs['data-index']);
                        const articleData = options.data?.[dataIndex];

                        if (!articleData) {
                            return domNode;
                        }

                        if (articleData.type === NewsArticleDataType.Article) {
                            return (
                                <a
                                    style={{
                                        color: getCurrentTheme().palette.primary.main,
                                        cursor: 'pointer',
                                    }}
                                    href={domNode.attribs.href}
                                    onClick={() => options.onArticleClick?.(articleData)}
                                >
                                    {articleData.title}
                                </a>
                            );
                        } else {
                            const quote = options.articleQuotes?.find((quote) => quote.symbol === articleData.value);
                            if (!quote) {
                                return domNode;
                            }

                            return (
                                <span
                                    style={{
                                        color:
                                            quote.changePercent > 0
                                                ? getCurrentTheme().palette.market.up
                                                : getCurrentTheme().palette.market.down,
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <a
                                        style={{
                                            color: getCurrentTheme().palette.primary.main,
                                            fontWeight: 700,
                                        }}
                                        onClick={() => options.onSymbolClick?.(quote.symbol)}
                                    >
                                        {articleData.value}:&nbsp;
                                    </a>
                                    {formatPercentage(quote.changePercent)}
                                </span>
                            );
                        }
                    }

                    return domNode;
                },
            },
        );
    } else {
        return parse(body);
    }
};
